import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import styles from './stylesheets/MetadataInfo.module.css';
import {
  wcaGetWhatsappProfile,
  wcaSetWhatsappProfile,
  wcaUploadProfilePicture,
} from '../../../reducers/wca/setting/setting.action';
import { CustomButton } from '../../../components/CustomButton';
import { useLayout } from '../../../context/LayoutContext';
import { ReactComponent as Edit } from '../../../svg/edit.svg';
import Loader from '../../../components/Loader';
import WhatsappProfileView from './components/  WhatsappProfileView';
import WhatsappProfileEdit from './components/WhatsappProfileEdit';

const WhatsappProfile = ({ metadataId }) => {
  const dispatch = useDispatch();
  const { toast } = useLayout();

  const [saving, setSaving] = useState(false);
  const [profilePictureFile, setProfilePictureFile] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [imageChanged, setImageChanged] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      messaging_product: 'whatsapp',
      about: '',
      address: '',
      description: '',
      email: '',
      vertical: 'OTHER',
      websites: [{ value: '' }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'websites',
  });

  useEffect(() => {
    if (metadataId) {
      fetchProfile();
    }
  }, [metadataId]);

  const fetchProfile = async () => {
    setLoadingProfile(true);
    try {
      const res = await dispatch(wcaGetWhatsappProfile({ metadataid: metadataId })).unwrap();

      if (res?.length > 0) {
        const profile = res[0];

        reset({
          messaging_product: profile.messaging_product || 'whatsapp',
          about: profile.about || '',
          address: profile.address || '',
          description: profile.description || '',
          email: profile.email || '',
          vertical: profile.vertical || 'OTHER',
          websites:
            profile.websites?.length > 0
              ? profile.websites.map((w) => ({ value: w }))
              : [{ value: '' }],
        });

        if (profile.profile_picture_url) {
          setProfilePicturePreview(`${profile.profile_picture_url}?t=${Date.now()}`);
        }
      }
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        detail: 'Failed to fetch WhatsApp profile',
      });
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleProfilePictureChange = (file) => {
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.current?.show({
        severity: 'error',
        detail: 'Image must be less than 5MB',
      });
      return;
    }

    setProfilePictureFile(file);
    setImageChanged(true);

    const reader = new FileReader();
    reader.onloadend = () => {
      setProfilePicturePreview(reader.result);
    };

    reader.readAsDataURL(file);
  };

  const onSubmit = async (data) => {
    setSaving(true);

    try {
      let profilePictureHandle;

      if (imageChanged && profilePictureFile) {
        const uploadRes = await dispatch(
          wcaUploadProfilePicture({
            metadataid: metadataId,
            file: profilePictureFile,
          }),
        ).unwrap();

        profilePictureHandle = uploadRes?.h;
      }

      const payload = {
        ...data,
        websites: data.websites.map((w) => w.value).filter((w) => w.trim() !== ''),
      };

      if (profilePictureHandle) {
        payload.profile_picture_handle = profilePictureHandle;
      }

      await dispatch(
        wcaSetWhatsappProfile({
          metadataid: metadataId,
          data: payload,
        }),
      ).unwrap();

      toast.current?.show({
        severity: 'success',
        detail: 'WhatsApp profile saved successfully',
      });

      setIsEditing(false);
      setImageChanged(false);
      setProfilePictureFile(null);
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        detail: 'Failed to save WhatsApp profile',
      });
    } finally {
      setSaving(false);
    }
  };

  const formData = watch();

  const businessCategoryOptions = [
    { label: 'Alcoholic Beverages', value: 'ALCOHOL' },
    { label: 'Clothing and Apparel', value: 'APPAREL' },
    { label: 'Automotive', value: 'AUTO' },
    { label: 'Beauty, Spa and Salon', value: 'BEAUTY' },
    { label: 'Education', value: 'EDU' },
    { label: 'Entertainment', value: 'ENTERTAIN' },
    { label: 'Event Planning and Service', value: 'EVENT_PLAN' },
    { label: 'Finance and Banking', value: 'FINANCE' },
    { label: 'Public Service', value: 'GOVT' },
    { label: 'Food and Grocery', value: 'GROCERY' },
    { label: 'Medical and Health', value: 'HEALTH' },
    { label: 'Hotel and Lodging', value: 'HOTEL' },
    { label: 'Non-profit', value: 'NONPROFIT' },
    { label: 'Online Gambling & Gaming', value: 'ONLINE_GAMBLING' },
    { label: 'Over-the-Counter Drugs', value: 'OTC_DRUGS' },
    { label: 'Non-Online Gambling & Gaming', value: 'PHYSICAL_GAMBLING' },
    { label: 'Professional Services', value: 'PROF_SERVICES' },
    { label: 'Restaurant', value: 'RESTAURANT' },
    { label: 'Shopping and Retail', value: 'RETAIL' },
    { label: 'Travel and Transportation', value: 'TRAVEL' },
    { label: 'Other', value: 'OTHER' },
  ];

  return (
    <div className={styles.section}>
      <Loader visible={saving || loadingProfile} />
      <div className={styles.header}>
        <h3>WhatsApp Profile Configuration</h3>
        <div className="flex justify-content-end">
          {!isEditing && (
            <CustomButton
              icon={<Edit />}
              varient="icon-button"
              onClick={() => setIsEditing(true)}
            />
          )}
        </div>
      </div>

      {!isEditing ? (
        <WhatsappProfileView formData={formData} profilePicturePreview={profilePicturePreview} />
      ) : (
        <WhatsappProfileEdit
          control={control}
          errors={errors}
          watch={watch}
          fields={fields}
          append={append}
          remove={remove}
          profilePicturePreview={profilePicturePreview}
          handleProfilePictureChange={handleProfilePictureChange}
          handleSubmit={handleSubmit}
          onSubmit={onSubmit}
          setIsEditing={setIsEditing}
          saving={saving}
          businessCategoryOptions={businessCategoryOptions}
        />
      )}
    </div>
  );
};

export default WhatsappProfile;
